// src/services/socketService.js
import { io } from 'socket.io-client';

class SocketService {
    constructor() {
        this.socket = null;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.currentOrganizationId = null;
        this.connectionPromise = null;
    }

    connect() {
        // Return existing connection promise if connection is in progress
        if (this.connectionPromise) {
            return this.connectionPromise;
        }

        // Return resolved promise if already connected
        if (this.socket?.connected) {
            return Promise.resolve();
        }

        this.connectionPromise = new Promise((resolve, reject) => {
            try {
                // Determine the correct socket URL based on environment
                const socketUrl = window.location.hostname === 'localhost' 
                    ? 'http://localhost:3001'
                    : window.location.origin; // Use the same origin for production
                
                this.socket = io(socketUrl, {
                    withCredentials: true,
                    reconnection: true,
                    reconnectionDelay: 1000,
                    reconnectionDelayMax: 5000,
                    reconnectionAttempts: this.maxReconnectAttempts
                });

                this.socket.on('connect', () => {
                    console.log('Connected to WebSocket server');
                    this.reconnectAttempts = 0;
                    if (this.currentOrganizationId) {
                        this.joinOrganization(this.currentOrganizationId);
                    }
                    this.connectionPromise = null;
                    resolve();
                });

                this.socket.on('disconnect', (reason) => {
                    console.log('Disconnected from WebSocket server:', reason);
                    this.connectionPromise = null;
                });

                this.socket.on('connect_error', (error) => {
                    console.error('Socket connection error:', error);
                    this.reconnectAttempts++;
                    
                    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
                        console.error('Max reconnection attempts reached');
                        this.socket.disconnect();
                        this.connectionPromise = null;
                        reject(new Error('Max reconnection attempts reached'));
                    }
                });

                this.socket.on('error', (error) => {
                    console.error('Socket error:', error);
                    this.connectionPromise = null;
                    reject(error);
                });

            } catch (error) {
                this.connectionPromise = null;
                reject(error);
            }
        });

        return this.connectionPromise;
    }

    joinOrganization(organizationId) {
        return new Promise((resolve, reject) => {
            if (!this.socket) {
                reject(new Error('Socket not initialized'));
                return;
            }
            
            this.currentOrganizationId = organizationId;
            this.socket.emit('join_organization', organizationId);
            resolve();
        });
    }

    onResponseUpdate(callback) {
        if (!this.socket) {
            console.error('Socket not initialized');
            return () => {}; // Return empty cleanup function
        }
        
        this.socket.on('response_updated', (data) => {
            console.log('Response update received:', data);
            callback(data);
        });

        // Return cleanup function
        return () => {
            this.socket?.off('response_updated');
        };
    }

    onCommentUpdate(callback) {
        if (!this.socket) {
            console.error('Socket not initialized');
            return () => {};
        }
        
        this.socket.on('comment_updated', (data) => {
            console.log('Comment update received:', data);
            callback(data);
        });

        return () => {
            this.socket?.off('comment_updated');
        };
    }

    onReactionUpdate(callback) {
        if (!this.socket) {
            console.error('Socket not initialized');
            return () => {};
        }
        
        this.socket.on('reaction_updated', (data) => {
            console.log('Reaction update received:', data);
            callback(data);
        });

        return () => {
            this.socket?.off('reaction_updated');
        };
    }

    onError(callback) {
        if (!this.socket) {
            console.error('Socket not initialized');
            return () => {};
        }
        
        this.socket.on('error', callback);
        return () => {
            this.socket?.off('error');
        };
    }

    onReconnect(callback) {
        if (!this.socket) {
            console.error('Socket not initialized');
            return () => {};
        }
        
        this.socket.on('reconnect', callback);
        return () => {
            this.socket?.off('reconnect');
        };
    }

    disconnect() {
        return new Promise((resolve) => {
            if (this.socket) {
                this.socket.removeAllListeners();
                this.socket.disconnect();
                this.socket = null;
                this.currentOrganizationId = null;
                this.reconnectAttempts = 0;
                this.connectionPromise = null;
            }
            resolve();
        });
    }

    isConnected() {
        return this.socket?.connected || false;
    }

    emit(event, data) {
        return new Promise((resolve, reject) => {
            if (!this.socket) {
                reject(new Error('Socket not initialized'));
                return;
            }
            
            this.socket.emit(event, data, (response) => {
                if (response?.error) {
                    reject(new Error(response.error));
                } else {
                    resolve(response);
                }
            });
        });
    }
}

// Create and export a singleton instance
const socketService = new SocketService();
export default socketService;